import { memo } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ParagraphHighliter from "./paragraphHighliter";
import { useTranslation } from "react-i18next";

const apiUrl = process.env.REACT_APP_MEDIA_URL;

const actionButtonSx = {
  minWidth: 0,
  px: 1,
  py: 0.5,
  fontSize: 11.5,
  lineHeight: 1.2,
  "& .MuiButton-startIcon": {
    mr: 0.5,
    ml: 0,
  },
  "& .MuiButton-startIcon > *": {
    fontSize: "18px !important",
  },
};

const actionLabelSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const ProductCard = ({
  data,
  content,
  handleClickOpen,
  handleModify,
  viewProduct,
  noModify,
  handleProducts,
  handleDiscount,
  onViewDetails,
  search,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isLightMode = theme.palette.mode === "light";
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const hasDiscount = data.discountValue && data.discountValue > 0;
  const mediaBg = isLightMode ? "#f2f2f5" : "rgba(255,255,255,0.04)";

  const actions = [];
  if (!noModify) {
    actions.push({
      key: "delete",
      label: t("common.delete"),
      color: "error",
      icon: <DeleteForeverIcon fontSize="small" />,
      onClick: handleClickOpen,
    });
    actions.push({
      key: "edit",
      label: t("common.edit"),
      color: "warning",
      icon: <EditIcon fontSize="small" />,
      onClick: handleModify,
    });
  }
  if (handleProducts) {
    actions.push({
      key: "view",
      label: t("common.viewProducts"),
      color: "info",
      icon: <VisibilityIcon fontSize="small" />,
      onClick: handleProducts,
    });
  }
  if (!noModify && handleDiscount) {
    actions.push({
      key: "discount",
      label: t("common.discount"),
      color: hasDiscount ? "success" : "info",
      icon: <LocalOfferIcon fontSize="small" />,
      onClick: () => handleDiscount(data),
    });
  }

  return (
    <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: "flex" }}>
      <Card
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: isLightMode
              ? "0 8px 20px rgba(0,0,0,0.10)"
              : "0 8px 20px rgba(0,0,0,0.45)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            cursor: viewProduct ? "pointer" : "default",
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            minHeight: 0,
          }}
          onClick={viewProduct}
        >
          {onViewDetails && (
            <Tooltip title={t("common.viewDetails")}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewDetails(data);
                }}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  bgcolor: "rgba(0,0,0,0.45)",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.65)" },
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {(data.image || data.logo) && (
            <CardMedia
              component="div"
              sx={{
                height: 150,
                backgroundColor: mediaBg,
                backgroundImage: `url(${apiUrl}/${(
                  data.image || data.logo
                ).replace(/\\/g, "/")})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                backgroundSize: "contain",
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
              image={`${apiUrl}/${(data.image || data.logo).replace(
                /\\/g,
                "/"
              )}`}
            />
          )}
          <CardContent sx={{ flexGrow: 1, pt: 1.5, pb: 1, px: 1.75 }}>
            <Typography
              gutterBottom
              variant="subtitle1"
              component="h2"
              sx={{ fontWeight: 650, lineHeight: 1.3, mb: 0.75 }}
            >
              <ParagraphHighliter title={data.name} search={search} />
            </Typography>
            <Box sx={{ display: "grid", gap: 0.4 }}>{content}</Box>
          </CardContent>
        </Box>
        {actions.length > 0 && (
          <CardActions
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${actions.length}, minmax(0, 1fr))`,
              gap: 0.75,
              px: 1.5,
              pb: 1.5,
              pt: 0,
              mt: "auto",
            }}
          >
            {actions.map((action) => {
              const showLabel = !isNarrow;
              return (
                <Tooltip key={action.key} title={action.label}>
                  <Button
                    variant="outlined"
                    color={action.color}
                    size="small"
                    startIcon={action.icon}
                    onClick={action.onClick}
                    sx={{
                      ...actionButtonSx,
                      ...(!showLabel
                        ? { "& .MuiButton-startIcon": { m: 0 } }
                        : null),
                    }}
                  >
                    {showLabel ? (
                      <Box component="span" sx={actionLabelSx}>
                        {action.label}
                      </Box>
                    ) : null}
                  </Button>
                </Tooltip>
              );
            })}
          </CardActions>
        )}
      </Card>
    </Grid>
  );
};

export default memo(ProductCard);
